import ExcelJS from "exceljs";
import {
  downloadWorkbook,
  getCompanyName,
  styleLightHeader,
  applyDataBorder,
  addTitleBlock,
  applyHeaderBasedColumnWidths,
  fileMonthLabel,
  formatDateCell,
  REPORT_COLORS,
} from "./payrollReportExcelShared";

function round2(n) {
  return Math.round(Number(n || 0) * 100) / 100;
}

/**
 * Structure Fixed package report (as-of pay month) through Gross / Deduction / Net.
 */
export async function exportAssignedStructureReportExcel(data = {}) {
  const rows = Array.isArray(data.rows) ? data.rows : [];
  if (!rows.length) throw new Error("No structure rows to export for this month / filter");

  const payMonth = String(data.payMonth || "").slice(0, 7);
  const monthLabel = fileMonthLabel(payMonth);
  const structureFilter = data.structureFilter || "All";
  const componentColumns = Array.isArray(data.componentColumns)
    ? data.componentColumns
    : [];

  const headers = [
    "Sl No",
    "Emp Code",
    "Name",
    "Designation",
    "Department",
    "Status",
    "DOJ",
    "Employee CTC",
    "Structure CTC",
    "Mode",
    "Structure",
    "Effective From",
    "Structure Status",
    ...componentColumns,
    "Gross (Fixed)",
    "Total Deductions",
    "Net (Fixed)",
    "Employer Benefits",
  ];

  const wb = new ExcelJS.Workbook();
  wb.creator = "PaceHRM";
  wb.created = new Date();
  const ws = wb.addWorksheet("Structure Report", {
    views: [{ state: "frozen", ySplit: 5, xSplit: 3 }],
  });

  addTitleBlock(
    ws,
    getCompanyName(),
    `Assigned Structure Report — ${monthLabel}`,
    `As-of pay month Fixed package (not Process paidDays). Structure filter: ${structureFilter}  |  Employees: ${rows.length}  |  ${new Date().toLocaleString("en-IN")}`,
    headers.length
  );

  const header = ws.addRow(headers);
  header.height = 28;
  header.eachCell((cell) => styleLightHeader(cell, REPORT_COLORS.structure));

  const moneyCols = new Set([
    8, // Employee CTC
    9, // Structure CTC
    ...componentColumns.map((_, i) => 14 + i),
    14 + componentColumns.length, // Gross
    15 + componentColumns.length, // Deductions
    16 + componentColumns.length, // Net
    17 + componentColumns.length, // Employer
  ]);

  const moneyNumFmt = "#,##0.00";

  rows.forEach((r, idx) => {
    const amounts = r.amountsByName || {};
    const line = ws.addRow([
      idx + 1,
      r.empCode || "",
      r.name || "",
      r.designation || "",
      r.department || "",
      r.employmentStatus || "",
      formatDateCell(r.joiningDate),
      round2(r.employeeCtc),
      round2(r.annualCtc),
      r.mode || "",
      r.structureName || "",
      formatDateCell(r.effectiveFrom),
      r.structureStatus || "",
      ...componentColumns.map((n) =>
        amounts[n] != null && amounts[n] !== "" ? round2(amounts[n]) : ""
      ),
      round2(r.grossPay),
      round2(r.totalDeductions),
      round2(r.netPay),
      round2(r.employerBenefits),
    ]);
    line.eachCell((cell, col) => {
      applyDataBorder(cell);
      cell.font = { size: 10, color: { argb: "FF111827" } };
      const leftish = col <= 7 || col === 10 || col === 11 || col === 12 || col === 13;
      cell.alignment = {
        vertical: "middle",
        horizontal: leftish ? "left" : "right",
      };
      if (moneyCols.has(col) && cell.value !== "" && cell.value != null) {
        cell.numFmt = moneyNumFmt;
      }
    });
  });

  const tot = ws.addRow([
    "",
    "",
    `TOTAL (${rows.length})`,
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    ...componentColumns.map((n) =>
      round2(rows.reduce((s, r) => s + (Number(r.amountsByName?.[n]) || 0), 0))
    ),
    round2(rows.reduce((s, r) => s + (Number(r.grossPay) || 0), 0)),
    round2(rows.reduce((s, r) => s + (Number(r.totalDeductions) || 0), 0)),
    round2(rows.reduce((s, r) => s + (Number(r.netPay) || 0), 0)),
    round2(rows.reduce((s, r) => s + (Number(r.employerBenefits) || 0), 0)),
  ]);
  tot.font = { bold: true, size: 10 };
  tot.eachCell((cell, col) => {
    applyDataBorder(cell);
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFEF3C7" } };
    if (moneyCols.has(col) && cell.value !== "" && cell.value != null) {
      cell.numFmt = moneyNumFmt;
    }
  });

  applyHeaderBasedColumnWidths(ws, headers, { min: 10, max: 28 });
  ws.getColumn(3).width = 22;
  ws.getColumn(11).width = 18;

  // Legend sheet
  const leg = wb.addWorksheet("Notes");
  leg.addRow(["Assigned Structure Report — Notes"]);
  leg.getRow(1).font = { bold: true, size: 12 };
  [
    "Amounts are monthly Fixed values from the salary structure effective for the selected pay month (as-of resolve).",
    "This is NOT Process paidDays / live PF segments. Use it to audit structure package (e.g. PF Employer Fixed) vs Payroll Register.",
    "Gross (Fixed) = sum of earning heads. Total Deductions = employee PF/ESI/PT and other deductions on structure. Net = Gross − Deductions.",
    "Employer Benefits (PF Er, Gratuity, ESI Er, etc.) are shown separately and excluded from Net.",
    `Structure filter applied: ${structureFilter}`,
    `Pay month: ${payMonth}`,
  ].forEach((t) => leg.addRow([t]));
  leg.getColumn(1).width = 110;

  const safe = String(monthLabel || payMonth || "month").replace(/[^\w.-]+/g, "_");
  const filt =
    structureFilter && !/^all$/i.test(structureFilter)
      ? `_${String(structureFilter).replace(/[^\w.-]+/g, "_")}`
      : "";
  return downloadWorkbook(wb, `Assigned_Structure_${safe}${filt}.xlsx`);
}
